import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";

const STILL = (i) => `/art/worlds/world_${i}.jpg`;

export default function Showcase() {
  const [captions, setCaptions] = useState([]);
  const [motionUrls, setMotionUrls] = useState([]);

  useEffect(() => {
    api.get("/film/worlds").then((r) => {
      setCaptions(r.data.captions || []);
      setMotionUrls(r.data.motion || []);
    }).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section
        data-testid="showcase-hero"
        className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 border-b border-white/[0.05]"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] opacity-[0.09] blur-3xl"
               style={{ background: "radial-gradient(circle at 50% 0%,#ff5a00,transparent 60%)" }} />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-10 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.45em] text-[#ff5a00]">The Showcase</p>
          <h1
            data-testid="showcase-headline"
            className="font-display uppercase leading-[0.92] mt-6 text-[#ededed]"
            style={{ fontSize: "clamp(2.5rem, 8vw, 6rem)", letterSpacing: "-0.005em" }}
          >
            Twelve worlds.
            <br />
            <span className="italic text-[#ff5a00]">Our receipts.</span>
          </h1>
          <p className="font-display italic text-lg sm:text-2xl text-[#a1a1aa] mt-8 max-w-3xl mx-auto leading-relaxed">
            Every image below was made in this forge. Every clip. Every world.
            No stock, no filler, no borrowed talent.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {captions.map((cap, i) => (
            <ShowTile
              key={i}
              index={i}
              name={cap.name}
              caption={cap.caption}
              motionUrl={motionUrls[i]}
              veiled={cap.veiled}
            />
          ))}
        </div>

        {!captions.length && (
          <p className="text-center font-mono text-xs uppercase tracking-[0.28em] text-[#52525b] py-24">
            Loading the reels…
          </p>
        )}
      </section>
    </Layout>
  );
}

function ShowTile({ index, name, caption, motionUrl, veiled }) {
  const [hover, setHover] = useState(false);
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 6) * 0.04 }}
      data-testid={`showcase-tile-${index}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group relative aspect-[4/5] overflow-hidden border border-[#27272a] bg-black"
    >
      {motionUrl && hover ? (
        <video
          key={motionUrl}
          src={motionUrl}
          poster={STILL(index)}
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div
          className="absolute inset-0 bg-center bg-cover transition-transform duration-[3000ms] ease-out group-hover:scale-105"
          style={{ backgroundImage: `url(${STILL(index)})` }}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-end p-5 sm:p-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#ff5a00]">
          {String(index + 1).padStart(2, "0")} · {veiled ? "Veiled" : name === "Aethelgard" ? "Live" : "Coming Soon"}
        </p>
        <h3 className="font-display uppercase text-2xl sm:text-3xl text-[#ededed] mt-2 leading-tight">
          {name}
        </h3>
        <p className="font-display italic text-sm sm:text-base text-[#a1a1aa] mt-2 leading-snug">
          {caption}
        </p>
      </div>
    </motion.article>
  );
}
